import type { ClockPort, TimerHandle } from '@lw/shared/clock';
import {
  ATTEMPT_TIMEOUT_FIRE_MS,
  ATTEMPT_TIMEOUT_MS,
  OUTBOX_MAX_AGE_MS,
  PRE_FLUSH_MIN_PINGS,
  RETRY_CAP_MS,
  RETRY_DELAYS_MS,
  RETRY_JITTER,
} from '@lw/shared/constants';
import type { TimeBlock } from '@lw/shared/time-sync';
import type { CommandType } from '@lw/shared/mission-fsm';
import type { ChronoClient } from './ChronoClient.js';

export type { CommandType } from '@lw/shared/mission-fsm';

export type Precondition = { basedOnSeq: number } | { expectedState: string } | null;

export interface PressRecord {
  bootId: string;
  epochId: string | null;
  pressedAtPerf: number;
  pressedAtWall: number;
  claimedT0: number | null;
  anchorUncMs: number | null;
  /** ChronoClient.pauseTotalMs() at press; null when the record predates this page lifetime */
  pauseCounterAtPress: number | null;
}

export interface OutboxRecord {
  cmdId: string;
  type: CommandType;
  missionId: string;
  role: 'UAV' | 'FDC';
  seq: number;
  payload: Record<string, unknown>;
  precondition: Precondition;
  press: PressRecord;
  createdAtWall: number;
  attempts: number;
  status: 'PENDING' | 'SENDING' | 'DONE' | 'DROPPED';
}

export interface CommandEnvelope {
  v: 1;
  cmdId: string;
  type: CommandType;
  missionId: string;
  deviceId: string;
  role: 'UAV' | 'FDC';
  clientBuild: string;
  seq: number;
  attempt: number;
  precondition: Precondition;
  time: TimeBlock;
  payload: Record<string, unknown>;
}

export interface Ack {
  v: 1;
  cmdId: string;
  status: 'APPLIED' | 'REJECTED_STALE' | 'REJECTED_INVALID' | 'REJECTED_AUTH' | 'REJECTED_NOT_SEAT';
  duplicate: boolean;
  seq: number;
  tsRx: number;
  snapshot: SnapshotLike;
}

export interface SnapshotLike {
  missionId: string;
  seq: number;
  state: string;
}

export interface OutboxStore {
  load(): OutboxRecord[];
  put(r: OutboxRecord): void;
  remove(cmdId: string): void;
}

export class MemoryOutboxStore implements OutboxStore {
  private m = new Map<string, OutboxRecord>();
  load(): OutboxRecord[] {
    return [...this.m.values()].map((r) => ({ ...r }));
  }
  put(r: OutboxRecord): void {
    this.m.set(r.cmdId, { ...r });
  }
  remove(cmdId: string): void {
    this.m.delete(cmdId);
  }
}

export type OutboxEvent =
  | { type: 'OUTBOX_ENQUEUED'; cmdId: string; cmd: CommandType; depth: number }
  | { type: 'OUTBOX_ATTEMPT'; cmdId: string; attempt: number; ageMonoMs: number | null; ageWallMs: number }
  | { type: 'OUTBOX_ATTEMPT_FAILED'; cmdId: string; attempt: number; reason: string; retryInMs: number }
  | { type: 'OUTBOX_ACKED'; cmdId: string; status: Ack['status']; duplicate: boolean }
  | { type: 'client_outbox_dropped'; cmdId: string; reason: 'MISSION_TERMINAL' | 'TOO_OLD' | 'SUPERSEDED' | 'OPERATOR' }
  | { type: 'OUTBOX_MINI_BURST_TIMEOUT'; cmdId: string };

export interface OutboxOptions {
  deviceId: string;
  bootId: string;
  clientBuild?: string;
  store?: OutboxStore;
  /**
   * Performs one HTTPS POST (or WS race) attempt; rejects on network error / timeout / abort.
   * The signal fires when a reconnect trigger arrives mid-attempt so the command is resent at
   * once with fresh ages (dedupe makes a doubly-delivered attempt harmless).
   */
  send: (env: CommandEnvelope, timeoutMs: number, signal: AbortSignal) => Promise<Ack>;
  onEvent?: (e: OutboxEvent) => void;
  onAck?: (ack: Ack, rec: OutboxRecord) => void;
  /** deterministic jitter source in [0,1) */
  random?: () => number;
  mintCmdId?: () => string;
  /** how long to wait for the pre-flush mini-burst before sending anyway */
  miniBurstTimeoutMs?: number;
}

/**
 * The offline outbox (ARCHITECTURE §8.5–8.7, §5.7.1): the press record is stamped synchronously
 * at enqueue, every attempt re-derives its ages, one command is in flight at a time, retries go on
 * forever with the same cmdId, and nothing is ever gated on connectivity.
 */
export class Outbox {
  private readonly clock: ClockPort;
  private readonly chrono: ChronoClient;
  private readonly opts: OutboxOptions;
  private readonly store: OutboxStore;
  private records: OutboxRecord[] = [];
  private seqCounter = 0;
  private inFlight: string | null = null;
  private retryHandle: TimerHandle | null = null;
  private flushing = false;
  private idCounter = 0;
  private wake = false;
  private abort: AbortController | null = null;

  constructor(clock: ClockPort, chrono: ChronoClient, opts: OutboxOptions) {
    this.clock = clock;
    this.chrono = chrono;
    this.opts = opts;
    this.store = opts.store ?? new MemoryOutboxStore();
    // records from a previous page lifetime: mono age and pause hint are unknown
    this.records = this.store
      .load()
      .filter((r) => r.status === 'PENDING' || r.status === 'SENDING')
      .map((r) => ({ ...r, status: 'PENDING' as const }))
      .sort((a, b) => a.seq - b.seq);
    this.seqCounter = this.records.reduce((m, r) => Math.max(m, r.seq), 0);
  }

  /** Stamp and persist the press — synchronously, before any I/O (§5.7.1). */
  enqueue(type: CommandType, missionId: string, role: 'UAV' | 'FDC', payload: Record<string, unknown> = {}, precondition: Precondition = null): OutboxRecord {
    const pressedAtPerf = this.clock.perfNow();
    const pressedAtWall = this.clock.wallNow();
    const valid = this.chrono.isValid();
    const rec: OutboxRecord = {
      cmdId: this.opts.mintCmdId?.() ?? `${this.opts.deviceId}-${++this.idCounter}-${pressedAtWall.toString(36)}`,
      type,
      missionId,
      role,
      seq: ++this.seqCounter,
      payload,
      precondition,
      press: {
        bootId: this.opts.bootId,
        epochId: this.chrono.currentEpochId(),
        pressedAtPerf,
        pressedAtWall,
        claimedT0: valid ? this.chrono.serverNow() : null,
        anchorUncMs: valid ? this.chrono.uncertainty() : null,
        pauseCounterAtPress: this.chrono.pauseTotalMs(),
      },
      createdAtWall: pressedAtWall,
      attempts: 0,
      status: 'PENDING',
    };
    this.records.push(rec);
    this.store.put(rec);
    this.emit({ type: 'OUTBOX_ENQUEUED', cmdId: rec.cmdId, cmd: type, depth: this.pending().length });
    this.flush();
    return rec;
  }

  pending(): OutboxRecord[] {
    return this.records.filter((r) => r.status === 'PENDING' || r.status === 'SENDING');
  }

  inFlightCmdId(): string | null {
    return this.inFlight;
  }

  /** Operator cancel of an unsent entry (§8.5). */
  cancelUnsent(cmdId: string): boolean {
    const r = this.records.find((x) => x.cmdId === cmdId && x.status === 'PENDING');
    if (!r) return false;
    this.drop(r, 'OPERATOR');
    return true;
  }

  /** §8.7 revalidation against a fresh snapshot. An attempt in flight is judged by its ack, not here. */
  revalidate(snapshot: SnapshotLike): void {
    const nowWall = this.clock.wallNow();
    for (const r of this.pending()) {
      if (r.status === 'SENDING') continue;
      if (r.missionId === snapshot.missionId && (snapshot.state === 'SAVED' || snapshot.state === 'DISCARDED')) this.drop(r, 'MISSION_TERMINAL');
      else if (nowWall - r.createdAtWall > OUTBOX_MAX_AGE_MS) this.drop(r, 'TOO_OLD');
      else if (r.precondition && 'basedOnSeq' in r.precondition && snapshot.seq > r.precondition.basedOnSeq && r.missionId === snapshot.missionId) this.drop(r, 'SUPERSEDED');
    }
  }

  private stopped = false;

  /** Page lifetime ends (unload / simulated reload): no further attempts from this instance. */
  stop(): void {
    this.stopped = true;
    if (this.retryHandle) this.clock.clearTimeout(this.retryHandle);
    this.retryHandle = null;
    this.abort?.abort();
  }

  /** Trigger a flush (WS open, online, visibility regain, successful poll, enqueue). Idempotent. */
  flush(): void {
    if (this.stopped) return;
    if (this.flushing) {
      // a reconnect trigger while an attempt hangs: abort it and resend immediately
      this.wake = true;
      this.abort?.abort();
      return;
    }
    if (this.retryHandle) {
      this.clock.clearTimeout(this.retryHandle);
      this.retryHandle = null;
    }
    void this.loop();
  }

  private async loop(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      for (;;) {
        if (this.stopped) return;
        const next = this.pending().sort((a, b) => a.seq - b.seq)[0];
        if (!next) return;
        next.status = 'SENDING';
        this.inFlight = next.cmdId;
        this.wake = false;
        // Mandatory order on reconnect (§8.5): ≥ 3-ping mini-burst, then recompute ages, then send.
        await this.miniBurst(next.cmdId);
        const result = await this.attempt(next);
        if (this.stopped) return;
        if (result === 'DONE') continue;
        if (this.wake) {
          // woken by a reconnect trigger: retry at once with fresh ages
          this.emit({ type: 'OUTBOX_ATTEMPT_FAILED', cmdId: next.cmdId, attempt: next.attempts, reason: result, retryInMs: 0 });
          continue;
        }
        // failed: schedule a retry and leave the loop; the retry re-enters flush()
        const delay = this.retryDelay(next.attempts);
        this.emit({ type: 'OUTBOX_ATTEMPT_FAILED', cmdId: next.cmdId, attempt: next.attempts, reason: result, retryInMs: delay });
        this.retryHandle = this.clock.setTimeout(() => {
          this.retryHandle = null;
          this.flush();
        }, delay);
        return;
      }
    } finally {
      this.flushing = false;
      this.inFlight = null;
    }
  }

  private async miniBurst(cmdId: string): Promise<void> {
    const before = this.chrono.samplesAccepted();
    const timeout = this.opts.miniBurstTimeoutMs ?? 1_500;
    await this.chrono.burstAndWait(PRE_FLUSH_MIN_PINGS, timeout);
    if (this.chrono.samplesAccepted() - before === 0) this.emit({ type: 'OUTBOX_MINI_BURST_TIMEOUT', cmdId });
  }

  private timeBlock(rec: OutboxRecord): TimeBlock {
    const sentAtPerf = this.clock.perfNow();
    const sentAtWall = this.clock.wallNow();
    const sameBoot = rec.press.bootId === this.opts.bootId;
    return {
      bootId: rec.press.bootId,
      epochId: rec.press.epochId,
      pressedAtPerf: rec.press.pressedAtPerf,
      pressedAtWall: rec.press.pressedAtWall,
      claimedT0: rec.press.claimedT0,
      anchorUncMs: rec.press.anchorUncMs,
      sentAtPerf,
      sentAtWall,
      ageMonoMs: sameBoot ? sentAtPerf - rec.press.pressedAtPerf : null,
      ageWallMs: sentAtWall - rec.press.pressedAtWall,
      pauseDetectedMs: sameBoot && rec.press.pauseCounterAtPress !== null ? this.chrono.pauseTotalMs() - rec.press.pauseCounterAtPress : null,
      rttBestMs: this.chrono.rttBest(),
      attempt: rec.attempts,
    };
  }

  private async attempt(rec: OutboxRecord): Promise<'DONE' | string> {
    rec.attempts += 1;
    rec.status = 'SENDING';
    this.inFlight = rec.cmdId;
    this.store.put(rec);
    const time = this.timeBlock(rec);
    const env: CommandEnvelope = {
      v: 1,
      cmdId: rec.cmdId,
      type: rec.type,
      missionId: rec.missionId,
      deviceId: this.opts.deviceId,
      role: rec.role,
      clientBuild: this.opts.clientBuild ?? '0.0.0',
      seq: rec.seq,
      attempt: rec.attempts,
      precondition: rec.precondition,
      time,
      payload: rec.payload,
    };
    this.emit({ type: 'OUTBOX_ATTEMPT', cmdId: rec.cmdId, attempt: rec.attempts, ageMonoMs: time.ageMonoMs, ageWallMs: time.ageWallMs });
    const timeoutMs = rec.type === 'FIRE' || rec.type === 'CANCEL_FIRE' ? ATTEMPT_TIMEOUT_FIRE_MS : ATTEMPT_TIMEOUT_MS;
    const controller = new AbortController();
    this.abort = controller;
    try {
      const ack = await this.withTimeout(this.opts.send(env, timeoutMs, controller.signal), timeoutMs, controller.signal);
      if (ack.cmdId !== rec.cmdId) return 'ACK_MISMATCH';
      rec.status = 'DONE';
      this.store.remove(rec.cmdId);
      this.records = this.records.filter((r) => r !== rec);
      this.emit({ type: 'OUTBOX_ACKED', cmdId: rec.cmdId, status: ack.status, duplicate: ack.duplicate });
      this.opts.onAck?.(ack, rec);
      this.revalidate(ack.snapshot);
      return 'DONE';
    } catch (err) {
      rec.status = 'PENDING';
      this.store.put(rec);
      return err instanceof Error ? err.message : String(err);
    } finally {
      if (this.abort === controller) this.abort = null;
      if (this.inFlight === rec.cmdId) this.inFlight = null;
    }
  }

  private withTimeout<T>(p: Promise<T>, ms: number, signal: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const h = this.clock.setTimeout(() => reject(new Error('TIMEOUT')), ms);
      signal.addEventListener('abort', () => {
        this.clock.clearTimeout(h);
        reject(new Error('ABORTED'));
      });
      p.then(
        (v) => {
          this.clock.clearTimeout(h);
          resolve(v);
        },
        (e: unknown) => {
          this.clock.clearTimeout(h);
          reject(e instanceof Error ? e : new Error(String(e)));
        },
      );
    });
  }

  /** 0 / 0.5 / 1 / 2 / 4 → 5 s cap, ± 20 % jitter (§8.5). */
  private retryDelay(attempts: number): number {
    const base = attempts - 1 < RETRY_DELAYS_MS.length ? (RETRY_DELAYS_MS[attempts - 1] as number) : RETRY_CAP_MS;
    const r = this.opts.random?.() ?? 0.5;
    return Math.round(base * (1 + RETRY_JITTER * (2 * r - 1)));
  }

  private drop(r: OutboxRecord, reason: 'MISSION_TERMINAL' | 'TOO_OLD' | 'SUPERSEDED' | 'OPERATOR'): void {
    r.status = 'DROPPED';
    this.store.remove(r.cmdId);
    this.records = this.records.filter((x) => x !== r);
    this.emit({ type: 'client_outbox_dropped', cmdId: r.cmdId, reason });
  }

  private emit(e: OutboxEvent): void {
    this.opts.onEvent?.(e);
  }
}
